<template>
  <!-- Compact: one row per agreement so a long list scans as a table. -->
  <div v-if="compact" class="card mb-2">
    <div class="card-body cg-compact d-flex align-items-center gap-3">
      <div class="flex-grow-1 min-w-0">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <h3
            class="cg-card-title cg-clickable cg-link text-truncate"
            @click="$emit('open', ppa.key)"
          >
            {{ ppa.name }}
          </h3>
          <ToneBadge :tone="ppa.statusTone" :label="ppa.status" />
        </div>
        <p class="mb-0 text-body-secondary small cg-tabular text-truncate">
          {{ ppa.country }} · {{ fmt(ppa.received) }} MWh delivered ·
          {{ fmt(ppa.stillExpected) }} MWh expected
        </p>
      </div>
      <MonthStrip :cells="ppa.deliveryCells" class="cg-compact-strip" />
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary cg-icon-btn"
        :aria-label="`Open ${ppa.name}`"
        title="Open agreement"
        @click="$emit('open', ppa.key)"
      >
        <font-awesome-icon icon="chevron-right" />
      </button>
    </div>
  </div>

  <div v-else class="card mb-3">
    <div class="card-body cg-tight">
      <div
        class="d-flex justify-content-between align-items-baseline flex-wrap gap-2"
      >
        <div class="d-flex align-items-baseline flex-wrap gap-2 min-w-0">
          <h3
            class="cg-card-title cg-clickable cg-link"
            @click="$emit('open', ppa.key)"
          >
            {{ ppa.name }}
          </h3>
          <p class="mb-0 text-body-secondary small">{{ ppa.counterparty }}</p>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary ms-auto text-nowrap"
          @click="$emit('open', ppa.key)"
        >
          Open agreement
        </button>
      </div>

      <div class="cg-halves">
        <div class="cg-half cg-rule-top">
          <p class="cg-micro mb-2">Agreement</p>
          <StatBlock :value="fmt(ppa.total)" unit="MWh forecast" class="mb-2" />
          <div class="d-flex flex-wrap gap-2">
            <ToneBadge tone="forest" label="Transfer to account" />
            <ToneBadge tone="pending" :label="ppa.cadence" />
            <ToneBadge
              tone="pending"
              :label="`${ppa.devices.length} ${ppa.devices.length === 1 ? 'device' : 'devices'}`"
            />
          </div>
        </div>
        <div class="cg-half cg-rule-top">
          <div class="d-flex justify-content-between align-items-baseline mb-2">
            <p class="cg-micro mb-0">Delivery</p>
            <ToneBadge :tone="ppa.statusTone" :label="ppa.status" />
          </div>
          <div class="d-flex flex-wrap gap-3 mb-2">
            <StatBlock
              :value="fmt(ppa.received)"
              :unit="`MWh delivered (${ppa.pct})`"
            />
            <StatBlock
              :value="fmt(ppa.stillExpected)"
              unit="MWh still expected"
              class="text-body-secondary"
            />
          </div>
          <MonthStrip :cells="ppa.deliveryCells" labels />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PpaKey } from "@/compliance_groups/fixture/complianceGroupsTypes";
import type { DerivedPpa } from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import MonthStrip from "./MonthStrip.vue";
import StatBlock from "./StatBlock.vue";
import ToneBadge from "./ToneBadge.vue";

withDefaults(defineProps<{ ppa: DerivedPpa; compact?: boolean }>(), {
  compact: false,
});
defineEmits<{ open: [key: PpaKey] }>();
</script>

<style scoped lang="scss">
.cg-half {
  flex: 1 1 220px;
  min-width: 0;
}
.cg-link:hover {
  text-decoration: underline;
}
// Compact row: tighter padding than the feature's card default, and a fixed-width strip so the
// cells line up down the list, wide enough that each month reads as a rectangle.
.card-body.cg-compact {
  padding: 10px 14px;
}
.cg-compact-strip {
  flex: 0 0 22rem;
}
.cg-icon-btn {
  flex: 0 0 auto;
  padding-inline: 0.5rem;
}
</style>
